import React, { useState } from 'react';
import { Download, Loader2 } from 'lucide-react';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import toast from 'react-hot-toast';
import { prepareForCapture } from './exportCapture';

interface ComponentExportButtonProps {
  targetRef: React.RefObject<HTMLDivElement | null>;
  filename: string;
  className?: string;
}

const ComponentExportButton: React.FC<ComponentExportButtonProps> = ({ targetRef, filename, className = '' }) => {
  const [exporting, setExporting] = useState<'png' | 'pdf' | null>(null);

  const exportAs = async (format: 'png' | 'pdf') => {
    if (!targetRef.current || exporting) return;
    setExporting(format);
    // The component is captured as laid out on screen, so anything a scroll container or a
    // height cap is hiding has to be opened up in the copy first (see exportCapture.ts).
    const capture = prepareForCapture(targetRef.current);
    try {
      const canvas = await html2canvas(targetRef.current, {
        backgroundColor: '#ffffff',
        scale: 2,
        onclone: capture.onclone,
      });
      if (format === 'png') {
        const link = document.createElement('a');
        link.download = `${filename}.png`;
        link.href = canvas.toDataURL();
        link.click();
      } else {
        const pdf = new jsPDF({
          orientation: canvas.width > canvas.height ? 'landscape' : 'portrait',
          unit: 'px',
          format: [canvas.width, canvas.height],
        });
        pdf.addImage(canvas.toDataURL('image/png'), 'PNG', 0, 0, canvas.width, canvas.height);
        pdf.save(`${filename}.pdf`);
      }
      toast.success(`Exported as ${format.toUpperCase()}`);
    } catch {
      toast.error('Export failed');
    } finally {
      capture.restore();
      setExporting(null);
    }
  };

  return (
    // The export buttons are controls, not content: html2canvas skips anything marked this way,
    // so the client's picture does not contain a "PNG PDF" button.
    <div data-html2canvas-ignore="true" className={`flex items-center gap-1 ${className}`}>
      {(['png', 'pdf'] as const).map(format => (
        <button
          key={format}
          onClick={(e) => { e.stopPropagation(); exportAs(format); }}
          disabled={!!exporting}
          title={`Export this section as ${format.toUpperCase()}`}
          className="flex items-center gap-1 px-2 py-1 text-[11px] font-medium text-gray-600 bg-white border border-gray-200 rounded-md hover:bg-gray-50 transition-colors disabled:opacity-60"
        >
          {exporting === format ? <Loader2 className="w-3 h-3 animate-spin" /> : <Download className="w-3 h-3" />}
          {format.toUpperCase()}
        </button>
      ))}
    </div>
  );
};

export default ComponentExportButton;
